'use client';

import { cn } from '@ahm/common-helpers';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

interface ItemDescriptionProps {
  itemId: string;
  description: string;
  isLoading?: boolean;
  isSoloMeal?: boolean;
  enableTypingEffect?: boolean;
}

export function ItemDescription({
  itemId: _itemId,
  description,
  isLoading = false,
  isSoloMeal = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  enableTypingEffect = false,
}: ItemDescriptionProps) {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset when description changes
  useEffect(() => {
    if (description !== displayText) {
      if (enableTypingEffect) {
        setDisplayText('');
        setCurrentIndex(0);
      } else {
        // Direct display without typing effect
        setDisplayText(description);
      }
    }
  }, [description, displayText, enableTypingEffect]);

  // Typing effect when description is available and enabled
  useEffect(() => {
    if (!description || isLoading || !enableTypingEffect) return;

    if (currentIndex < description.length) {
      const timer = setTimeout(() => {
        setDisplayText(description.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 30); // Adjust speed here

      return () => clearTimeout(timer);
    }
  }, [description, currentIndex, isLoading, enableTypingEffect]);

  // Show loading dots when waiting for description
  const showLoading = isLoading || (!description && currentIndex === 0);

  if (!description && !showLoading) {
    return null;
  }

  return (
    <div
      className={cn(
        'text-sm leading-relaxed font-normal flex items-center rounded-lg py-2 px-4 mt-2 relative min-h-10 bg-gradient-to-r from-[#FFF8ED] via-[#ECF3F8] to-[#EAF2F9]',
        showLoading && 'animate-ai-gradient w-[100px]'
      )}
    >
      {!showLoading && (
        <img
          src="/static/icons/ai.svg"
          alt="Description"
          className="inline-block w-6 h-auto mt-0.5 shrink-0 -top-2 left-0 absolute"
        />
      )}
      <div className="ml-1.5 text-xs flex-1">
        {showLoading ? (
          <div className="ai-wave-loader justify-center">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        ) : (
          <>
            <div className="whitespace-pre-wrap">
              {/* @ts-expect-error react-markdown children prop */}
              <ReactMarkdown>{displayText}</ReactMarkdown>
            </div>
            {enableTypingEffect && currentIndex < description.length && (
              <span className="animate-pulse">|</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
